import React, { useState } from 'react';
import { Check, RotateCcw, Palette } from 'lucide-react';
import {
  ACCENT_PRESETS, TITLE_PRESETS, DEFAULT_ACCENT, DEFAULT_INK, normalizeHex,
} from '../lib/colorPalettes';

// ───────────────────────────────────────────────────────────────────────────
// ColorPaletteSelector — pick the dossier's highlight (accent) and title (ink)
// colors for the Swiss Grid template.
//
//   - accent presets + custom picker → recolors every highlight element
//     (section numbers, chips, language badges, date pills, seniority, dot).
//   - title presets + custom picker  → recolors the trigram + section titles.
//
// `value` is { primary, accent } (primary = ink/title, accent = highlight), or
// null for "use template defaults". onChange receives the merged override;
// onClear resets to defaults.
// ───────────────────────────────────────────────────────────────────────────

const Dot = ({ color, size = 24, selected, onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    aria-label={label}
    style={{
      width: size, height: size, padding: 0,
      borderRadius: '50%',
      background: color,
      border: `2px solid ${selected ? '#111827' : 'transparent'}`,
      outline: '1px solid rgba(0,0,0,0.10)',
      cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
      transition: 'border-color 0.12s',
    }}
  >
    {selected && <Check size={size > 22 ? 13 : 11} color="#fff" strokeWidth={3} />}
  </button>
);

const CustomPicker = ({ value, fallback, onChange }) => {
  const [draft, setDraft] = useState(value || '');
  React.useEffect(() => { setDraft(value || ''); }, [value]);

  const commit = (raw) => {
    const norm = normalizeHex(raw);
    if (norm) onChange(norm);
    else setDraft(value || '');
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 11, color: '#6b7280' }}>Personnalisé</span>
      <span style={{ position: 'relative', display: 'inline-block', width: 24, height: 24 }}>
        <span style={{
          position: 'absolute', inset: 0, borderRadius: 6,
          background: normalizeHex(draft) || fallback,
          border: '1px solid #e5e7eb',
        }} />
        <input
          type="color"
          value={normalizeHex(draft) || fallback}
          onChange={(e) => { setDraft(e.target.value); onChange(e.target.value); }}
          style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }}
          aria-label="Sélecteur de couleur personnalisée"
        />
      </span>
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        placeholder={fallback}
        style={{
          width: 84, padding: '5px 7px', borderRadius: 6, border: '1px solid #e5e7eb',
          fontSize: 11, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          color: '#111827', background: '#fff', outline: 'none',
        }}
      />
    </div>
  );
};

const ColorPaletteSelector = ({ value, onChange, onClear }) => {
  const current = value || {};
  // For selection highlighting, treat an unset color as the template default.
  const effAccent = normalizeHex(current.accent) || DEFAULT_ACCENT;
  const effInk = normalizeHex(current.primary) || DEFAULT_INK;

  const setAccent = (hex) => onChange({ ...current, accent: hex });
  const setInk = (hex) => onChange({ ...current, primary: hex });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#111827' }}>
          <Palette size={13} /> Couleurs
        </div>
        {value && onClear && (
          <button
            type="button"
            onClick={onClear}
            style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', padding: 0, color: '#6b7280', fontSize: 11, cursor: 'pointer' }}
            title="Réinitialiser les couleurs du template"
          >
            <RotateCcw size={11} /> Réinitialiser
          </button>
        )}
      </div>

      {/* ── Accent / highlights ── */}
      <div>
        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8 }}>Couleur d'accent (highlights)</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          {ACCENT_PRESETS.map((p) => (
            <Dot key={p.id} color={p.color} size={24} label={p.label}
              selected={normalizeHex(p.color) === effAccent} onClick={() => setAccent(p.color)} />
          ))}
        </div>
        <CustomPicker value={current.accent} fallback={DEFAULT_ACCENT} onChange={setAccent} />
      </div>

      {/* ── Title / ink ── */}
      <div style={{ paddingTop: 10, borderTop: '1px dashed #e5e7eb' }}>
        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8 }}>Couleur des titres</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          {TITLE_PRESETS.map((p) => (
            <Dot key={p.id} color={p.color} size={20} label={p.label}
              selected={normalizeHex(p.color) === effInk} onClick={() => setInk(p.color)} />
          ))}
        </div>
        <CustomPicker value={current.primary} fallback={DEFAULT_INK} onChange={setInk} />
      </div>
    </div>
  );
};

export default ColorPaletteSelector;
